import { DEFAULT_LANGUAGE, LanguageCode } from '@mkp/shared/models';
import { CreateUserPayload } from './create-user-payload';
import { UserDisplayType } from './user-display-type';
import { UserRole } from './user-role';
import { UserTitle } from './user-title';

interface LinkAttributes {
  methods: string[];
}

interface Link {
  href: string;
  templated: boolean;
  rel: string[];
  attributes: LinkAttributes;
}

interface UserLinks {
  self: Link;
  'profile-picture'?: Link; // Optional since not all users might have a profile picture
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  emailVerified: boolean;
  title: UserTitle;
  profilePictureMediaApiId: string | null;
  phoneNumber: string | null;
  role?: UserRole | undefined;
  _links?: UserLinks;
  settings: UserSettings;
  _version: string;
  _type: 'user';
}

export interface UserSettings {
  displayType: UserDisplayType;
  language: LanguageCode;
  marketingConsentAcceptedAt: Date | null;
  publicationAiUsageConsent: boolean | null;
}

export const defaultUser: User = {
  id: '',
  email: '',
  firstName: '',
  lastName: '',
  title: UserTitle.Ms,
  emailVerified: false,
  profilePictureMediaApiId: '',
  phoneNumber: '',
  settings: {
    displayType: UserDisplayType.CustomerJobcloud,
    marketingConsentAcceptedAt: null,
    publicationAiUsageConsent: null,
    language: DEFAULT_LANGUAGE.code,
  },
  _version: '',
  _type: 'user',
} as const;

interface CreateUserSettings {
  // the first marketing consent given is a boolean,
  // after that it is a date in marketingConsentAcceptedAt field.
  marketingConsent: boolean;
  displayType: UserDisplayType;
  language: LanguageCode;
}

type UpdateUserSettings = Partial<NonNullable<UserSettings & CreateUserSettings>>;

export type UpdateUser = Partial<Omit<User, 'settings'>> &
  Pick<User, 'id' | '_version'> & { settings?: UpdateUserSettings };

export const isUpdateUser = (user: UpdateUser | CreateUserPayload): user is UpdateUser =>
  (user as UpdateUser)?.id !== undefined;
